/**
 * The browser and operating system a captured user agent names (TOADO-907),
 * for the ticket's Capture details and for MCP agents.
 *
 * Kept small and in the repo rather than taken from a library: ua-parser-js
 * 2.x is AGPL, which Toado must not take on, and the few browsers a bug report
 * comes from are named by a handful of tokens. Order matters, because most
 * browsers also say "Chrome" or "Safari": Edge, Opera and Samsung Internet are
 * looked for before Chrome, and Chrome before Safari.
 *
 * What a user agent cannot say is not guessed. Chrome freezes Windows 10 and
 * 11 to "Windows NT 10.0" and every Mac to "Mac OS X 10_15_7", so those are
 * "Windows" and "macOS" with no version; and Safari on an iPad asks for the
 * desktop site as a Mac by default, so that reads "macOS" too. Exact versions
 * come only from a browser's client hints (`platformVersion`, `fullVersion`),
 * which `parseUserAgent` takes when the capture carries them.
 */

export interface ParsedUserAgent {
  browser?: { name: string; version?: string };
  os?: { name: string; version?: string };
}

/** Client hints a Chromium browser gives, when the capture asked for them. */
export interface UserAgentHints {
  platformVersion?: string;
  fullVersion?: string;
  architecture?: string;
  model?: string;
}

/**
 * The hint fields a stored browser can carry, as `cleanClientHints` keeps
 * them, and nothing else. Wherever a user agent is put in from somewhere other
 * than the page, these go: they were the page's, and cannot be checked
 * against it (z.ai and Codex, round 5).
 */
export const STORED_HINT_KEYS = ['platformVersion', 'fullVersion', 'architecture', 'model'] as const;

// A hint added to UserAgentHints and not to STORED_HINT_KEYS, or the reverse,
// stops this compiling (Codex, round 6). Not CLIENT_HINT_NAMES: those are
// what a page is asked for, and fullVersionList is never stored.
type StoredHintKey = typeof STORED_HINT_KEYS[number];
const storedHintKeysMatch: [keyof UserAgentHints] extends [StoredHintKey] ? ([StoredHintKey] extends [keyof UserAgentHints] ? true : false) : false = true;
void storedHintKeysMatch;

/** A stored browser without any client hint, for a user agent that did not come with them. */
export function withoutClientHints<T extends object>(browser: T | undefined): Omit<T, StoredHintKey> {
  const rest = { ...(browser ?? {}) } as Record<string, unknown>;
  for (const key of STORED_HINT_KEYS) delete rest[key];
  return rest as Omit<T, StoredHintKey>;
}

/** The high-entropy hints a capture asks a Chromium browser for. */
export const CLIENT_HINT_NAMES = ['platformVersion', 'fullVersionList', 'architecture', 'model'] as const;

const HINT_VERSION = /^\d{1,5}(?:\.\d{1,6}){0,3}$/;
const HINT_ARCHITECTURE = /^[a-z0-9_-]{1,16}$/i;
const HINT_MODEL = /^[A-Za-z0-9 ._+()/-]{1,64}$/;

/**
 * Characters a user agent has no use for and a forger does: controls (a line
 * break makes a copied "Branch: production" line of its own), and format
 * characters, which include every bidi mark and override, the zero-width
 * ones and the soft hyphen (they reorder or hide what is shown). Taken by
 * Unicode category so none is missed (z.ai, round 2), with the Hangul fillers,
 * which are letters but draw nothing, and the variation selectors (round 3);
 * built from code points so no such character sits in this source file.
 */
const INVISIBLE = new RegExp(`[\\p{Cc}\\p{Cf}\\p{Zl}\\p{Zp}${String.fromCharCode(0x115f, 0x1160, 0x3164, 0xffa0)}${String.fromCharCode(0xfe00)}-${String.fromCharCode(0xfe0f)}]`, 'gu');

/** The most of a user agent any of this reads: a browser's is a few hundred characters. */
export const USER_AGENT_READ_LIMIT = 1_000;

/** A user agent with those characters taken out, as the server stores it (Codex, round 1). */
export function plainUserAgent(userAgent: string): string {
  return userAgent.replace(INVISIBLE, '');
}

/** Text with any such character shown as a backslash, a u and its code point in hex, for a stored value that predates `plainUserAgent`. */
export function visibleControls(text: string): string {
  return text.replace(INVISIBLE, (c) => `\\u{${c.codePointAt(0)!.toString(16).toUpperCase()}}`);
}

/**
 * A user agent without the device Android names in it: everything in its
 * comment after the version ("Android 8.0.0; en-us; SM-G950F Build/R16NW"
 * reads "Android 8.0.0; K", as Chrome's reduced user agent does), for a
 * capture that keeps only metadata (Codex, rounds 1 and 2). An unterminated
 * comment loses everything to the end. One pass, never a search that starts
 * again from each Android: a long field of them took seconds (Codex, round 3).
 */
export function withoutDeviceModel(userAgent: string): string {
  const android = /\bAndroid [\d.]+/g;
  let out = '';
  let from = 0;
  for (let match = android.exec(userAgent); match; match = android.exec(userAgent)) {
    const end = match.index + match[0].length;
    out += userAgent.slice(from, end);
    from = end;
    if (userAgent[end] === ';') {
      const close = userAgent.indexOf(')', end);
      out += '; K';
      from = close === -1 ? userAgent.length : close;
    }
    android.lastIndex = Math.max(from, end);
  }
  return out + userAgent.slice(from);
}

/**
 * The hints in `value` that are what they claim to be, and nothing else
 * (TOADO-907): versions are dotted numbers, the architecture a short word, the
 * model letters, digits and a little punctuation, at most 64. A page or a visitor writes
 * these, so the client checks them as it sends and the server again as it
 * stores, with this one function.
 */
export function cleanClientHints(value: unknown): UserAgentHints {
  if (!value || typeof value !== 'object') return {};
  const v = value as Record<string, unknown>;
  const out: UserAgentHints = {};
  if (typeof v.platformVersion === 'string' && HINT_VERSION.test(v.platformVersion)) out.platformVersion = v.platformVersion;
  if (typeof v.fullVersion === 'string' && HINT_VERSION.test(v.fullVersion)) out.fullVersion = v.fullVersion;
  if (typeof v.architecture === 'string' && HINT_ARCHITECTURE.test(v.architecture)) out.architecture = v.architecture;
  const model = typeof v.model === 'string' ? v.model.trim() : '';
  if (HINT_MODEL.test(model)) out.model = model;
  return out;
}

/** The brands whose full version is the browser's, in the order they are looked for. */
const FULL_VERSION_BRANDS = ['Microsoft Edge', 'Opera', 'Google Chrome', 'Chromium'];

/**
 * The hints from `navigator.userAgentData.getHighEntropyValues(CLIENT_HINT_NAMES)`:
 * the full version is the named brand's in `fullVersionList` (the list also
 * carries Chromium and a made-up brand), then cleaned.
 */
export function clientHintsFrom(values: unknown): UserAgentHints {
  if (!values || typeof values !== 'object') return {};
  const v = values as Record<string, unknown>;
  const list = Array.isArray(v.fullVersionList) ? v.fullVersionList.slice(0, 10) : [];
  let fullVersion: unknown;
  for (const brand of FULL_VERSION_BRANDS) {
    const entry = list.find((item): item is { brand: unknown; version: unknown } => !!item && typeof item === 'object' && (item as { brand?: unknown }).brand === brand);
    if (entry) { fullVersion = entry.version; break; }
  }
  return cleanClientHints({ platformVersion: v.platformVersion, fullVersion, architecture: v.architecture, model: v.model });
}

const BROWSERS: ReadonlyArray<[name: string, pattern: RegExp]> = [
  ['Edge', /\bEdg(?:e|A|iOS)?\/(\d+(?:\.\d+)*)/],
  ['Opera', /\b(?:OPR|Opera)\/(\d+(?:\.\d+)*)/],
  ['Samsung Internet', /\bSamsungBrowser\/(\d+(?:\.\d+)*)/],
  ['Firefox', /\b(?:Firefox|FxiOS)\/(\d+(?:\.\d+)*)/],
  ['Chrome', /\b(?:Chrome|CriOS)\/(\d+(?:\.\d+)*)/],
  ['Safari', /\bVersion\/(\d+(?:\.\d+)*)(?: Mobile\/\S+)? Safari\//],
];

function majorOf(version: string): string {
  return version.split('.')[0]!;
}

/** A hinted OS version as people say it: "14.5.0" is 14.5, "15.0.0" is 15. */
function shortVersion(version: string | undefined): string | undefined {
  const match = version ? /^(\d+)(?:\.(\d+))?/.exec(version) : null;
  if (!match) return undefined;
  return match[2] && match[2] !== '0' ? `${match[1]}.${match[2]}` : match[1];
}

function osOf(ua: string, platformVersion: string | undefined): ParsedUserAgent['os'] {
  if (/\bCrOS\b/.test(ua)) return { name: 'ChromeOS' };
  const android = /\bAndroid (\d+(?:\.\d+)*)(; K\))?/.exec(ua);
  if (android) {
    // Chrome's reduced user agent says "Android 10; K)" whatever the version,
    // so that one is no version at all without the hint (Codex, round 1). A
    // version our own model redaction left beside a "K" is the real one
    // (round 2).
    const frozen = android[1] === '10' && !!android[2];
    const version = shortVersion(platformVersion) ?? (frozen ? undefined : shortVersion(android[1]));
    return version ? { name: 'Android', version } : { name: 'Android' };
  }
  const ipad = /\biPad\b.*?\bOS (\d+)(?:_(\d+))?/.exec(ua);
  if (ipad) return { name: 'iPadOS', version: ipad[2] ? `${ipad[1]}.${ipad[2]}` : ipad[1] };
  const iphone = /\b(?:iPhone|iPod)\b.*?\bOS (\d+)(?:_(\d+))?/.exec(ua);
  if (iphone) return { name: 'iOS', version: iphone[2] ? `${iphone[1]}.${iphone[2]}` : iphone[1] };
  const windows = /\bWindows NT (\d+\.\d+)/.exec(ua);
  if (windows) {
    // Chrome's client hint tells 11 from 10: 13 and up is Windows 11.
    if (windows[1] === '10.0' && platformVersion) {
      const major = Number(platformVersion.split('.')[0]);
      if (Number.isFinite(major) && major > 0) return { name: 'Windows', version: major >= 13 ? '11' : '10' };
    }
    const named: Record<string, string> = { '6.1': '7', '6.2': '8', '6.3': '8.1' };
    return named[windows[1]!] ? { name: 'Windows', version: named[windows[1]!] } : { name: 'Windows' };
  }
  if (/\bMacintosh\b|\bMac OS X\b/.test(ua)) {
    // The hint carries the real version; the user agent's is frozen.
    const hinted = shortVersion(platformVersion);
    return hinted ? { name: 'macOS', version: hinted } : { name: 'macOS' };
  }
  if (/\bLinux\b/.test(ua)) return { name: 'Linux' };
  return undefined;
}

export function parseUserAgent(userAgent: string | undefined, hints: UserAgentHints = {}): ParsedUserAgent {
  if (typeof userAgent !== 'string' || !userAgent) return {};
  const ua = userAgent.slice(0, 1_000);
  const out: ParsedUserAgent = {};
  for (const [name, pattern] of BROWSERS) {
    const match = pattern.exec(ua);
    if (!match) continue;
    const major = majorOf(match[1]!);
    // A hinted full version is taken only when it agrees with the user agent's
    // own major version: the page writes both (z.ai, round 1).
    const full = name === 'Chrome' || name === 'Edge' ? hints.fullVersion : undefined;
    const exact = full && /^\d+(?:\.\d+){1,3}$/.test(full) && majorOf(full) === major ? full : undefined;
    out.browser = { name, version: exact ?? major };
    break;
  }
  const os = osOf(ua, hints.platformVersion);
  if (os) out.os = os;
  return out;
}

/** "Chrome 141 on Windows 11", or as much of it as is known, or null. */
export function userAgentSummary(parsed: ParsedUserAgent): string | null {
  const browser = parsed.browser && [parsed.browser.name, parsed.browser.version].filter(Boolean).join(' ');
  const os = parsed.os && [parsed.os.name, parsed.os.version].filter(Boolean).join(' ');
  if (browser && os) return `${browser} on ${os}`;
  return browser || os || null;
}

/**
 * A stored capture's browser, with the browser and OS its user agent names
 * beside the raw string (TOADO-907), so an agent fixing a Safari-only or
 * Windows-only bug sees them without parsing it. Read as the API answers;
 * nothing extra is stored.
 */
export function withParsedUserAgent(browser: unknown): unknown {
  if (!browser || typeof browser !== 'object') return browser;
  const value = browser as Record<string, unknown>;
  if (typeof value.userAgent !== 'string') return browser;
  const parsed = parseUserAgent(value.userAgent, cleanClientHints(value));
  return {
    ...value,
    ...(parsed.browser ? { browserName: parsed.browser.name, ...(parsed.browser.version ? { browserVersion: parsed.browser.version } : {}) } : {}),
    ...(parsed.os ? { osName: parsed.os.name, ...(parsed.os.version ? { osVersion: parsed.os.version } : {}) } : {}),
  };
}
